import React from 'react';
import LoadingSpinner from '../LoadingSpinner';

interface ExportProgressModalProps {
  isOpen: boolean;
  progress: {
    current: number;
    total: number;
    percentage: number;
    currentPage: number;
    totalPages: number;
  };
  message?: string;
  title?: string;
  unitLabel?: string;
  /** Si hay texto, se muestra error y el botón de reintento (si onRetry existe) */
  errorMessage?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
}

const ExportProgressModal: React.FC<ExportProgressModalProps> = ({
  isOpen,
  progress,
  message = 'Obteniendo pedidos...',
  title = 'Exportando Pedidos',
  unitLabel = 'pedidos',
  errorMessage = null,
  onRetry,
  retrying = false
}) => {
  if (!isOpen) return null;

  const hasError = Boolean(errorMessage)

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-custom-blue">{title}</h2>
        </div>

        <div className="space-y-6">
          {/* Mensaje */}
          <p className={`text-center ${hasError ? 'text-red-700 font-medium' : 'text-gray-600'}`}>{message}</p>

          {hasError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 text-center"
            >
              {errorMessage}
            </div>
          )}

          {/* Barra de progreso */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div
              className={`h-4 rounded-full transition-all duration-300 ease-out ${hasError ? 'bg-amber-500' : 'bg-blue-600'}`}
              style={{ width: `${Math.min(progress.percentage, 100)}%` }}
            />
          </div>

          {/* Información de progreso */}
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold text-gray-900">
              {progress.current.toLocaleString()} de {progress.total.toLocaleString()} {unitLabel}
            </p>
            <p className="text-sm text-gray-600">
              Página {progress.currentPage} de {progress.totalPages}
            </p>
            <p className={`text-3xl font-bold mt-2 ${hasError ? 'text-amber-600' : 'text-custom-blue'}`}>
              {Math.round(progress.percentage)}%
            </p>
          </div>

          {hasError && onRetry && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Reintentar desde el paso con error"
              >
                {retrying ? 'Reintentando…' : 'Reintentar'}
              </button>
            </div>
          )}

          {/* Spinner */}
          {!hasError && (
            <div className="flex justify-center mt-4">
              <LoadingSpinner size="medium" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportProgressModal;

